import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { clientAPI } from '../utils/api';
import { getLocalizationConfig } from '../utils/localization';

const LocalizationContext = createContext();

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};

export const LocalizationProvider = ({ children }) => {
  const { user } = useAuth();
  const [clientLocale, setClientLocale] = useState(null);
  const [config, setConfig] = useState(getLocalizationConfig('AU')); // Default to Australia

  useEffect(() => {
    loadClientLocalization();
  }, [user]);

  const loadClientLocalization = async () => {
    if (user && user.role === 'client') {
      try {
        const response = await clientAPI.getProfile();
        const clientData = response.data.data;

        const locale = {
          country: clientData.country || 'AU',
          date_format: clientData.date_format || 'dd/mm/yyyy',
          currency_symbol: clientData.currency_symbol || '$',
          currency_code: clientData.currency_code || 'AUD',
          timezone: clientData.timezone || 'Australia/Sydney'
        };

        setClientLocale(locale);
        setConfig(getLocalizationConfig(locale.country));
      } catch (error) {
        console.error('Failed to load client localization:', error);
        // Fallback to Australian defaults
        setClientLocale({
          country: 'AU',
          date_format: 'dd/mm/yyyy',
          currency_symbol: '$',
          currency_code: 'AUD',
          timezone: 'Australia/Sydney'
        });
        setConfig(getLocalizationConfig('AU'));
      }
    } else {
      // For non-client users, use Australian defaults
      setClientLocale({
        country: 'AU',
        date_format: 'dd/mm/yyyy',
        currency_symbol: '$',
        currency_code: 'AUD',
        timezone: 'Australia/Sydney'
      });
      setConfig(getLocalizationConfig('AU'));
    }
  };

  const updateLocalization = async (newLocale) => {
    try {
      if (user && user.role === 'client') {
        await clientAPI.updateLocalization(newLocale);
      }

      setClientLocale(newLocale);
      setConfig(getLocalizationConfig(newLocale.country));
      return { success: true };
    } catch (error) {
      console.error('Failed to update localization:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    clientLocale,
    config,
    updateLocalization,
    refreshLocalization: loadClientLocalization
  };

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};